import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  Snackbar, 
  Alert,
  Card,
  CardContent,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import { db } from '../firebaseConfig';
import { collection, addDoc, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';

const Kiosk = () => {
  const [unitNumber, setUnitNumber] = useState(''); // State for unit number input
  const [selectedClass, setSelectedClass] = useState(''); // State for selected class
  const [classes, setClasses] = useState([]); // State to hold available classes
  const [openSnackbar, setOpenSnackbar] = useState(false); // State for Snackbar visibility
  const [snackbarMessage, setSnackbarMessage] = useState(''); // State for Snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // State for Snackbar severity
  const [isLoading, setIsLoading] = useState(false); // State for loading
  const [currentDate, setCurrentDate] = useState(''); // State for current date display

  // Get current EST date
  const getCurrentESTDate = () => {
    const now = new Date();
    const estDate = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}));
    return estDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  useEffect(() => {
    // Set current date
    setCurrentDate(getCurrentESTDate());

    // Fetch available classes
    const fetchClasses = async () => {
      try {
        const classesDocRef = doc(db, 'hvac/GenericData/Classes/Classes');
        const classesDocSnapshot = await getDoc(classesDocRef);

        if (classesDocSnapshot.exists()) {
          const classesData = classesDocSnapshot.data();
          setClasses(classesData.classes || []);
        } else {
          console.error('No classes document found!');
          setSnackbarMessage('No classes available. Please contact administrator.');
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
        setSnackbarMessage('Error loading classes. Please try again.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    };

    fetchClasses();
  }, []);

  // Validate unit number format (M##)
  const validateUnitNumber = (unit) => {
    const unitRegex = /^M\d{2}$/i; // Matches M followed by exactly 2 digits
    return unitRegex.test(unit);
  };

  // Find user by unit number
  const findUserByUnitNumber = async (unitNumber) => {
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        if (userData.unitNumber === unitNumber) {
          return { id: userDoc.id, ...userData };
        }
      }
      return null;
    } catch (error) {
      console.error('Error finding user:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateUnitNumber(unitNumber)) {
      setSnackbarMessage('Please enter a valid unit number (format: M##)');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    if (!selectedClass) {
      setSnackbarMessage('Please select a class');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    setIsLoading(true);

    try {
      // Find user by unit number
      const user = await findUserByUnitNumber(unitNumber);
      
      if (!user) {
        setSnackbarMessage(`No user found with unit number ${unitNumber}`);
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setIsLoading(false);
        return;
      }

      // Create class entry data
      const classEntryData = {
        Date: getCurrentESTDate(),
        ClassName: selectedClass,
        Email: user.email,
        EntryType: 'Class',
        UnitNumber: unitNumber,
        UserName: user.displayName || user.email,
        Timestamp: new Date()
      };

      // Submit to ClassData collection
      const docId = new Date().toISOString();
      const docRef = doc(collection(db, 'hvac', 'ClassData', 'Responses'), docId);
      await setDoc(docRef, classEntryData);

      setSnackbarMessage(`Successfully logged ${selectedClass} for ${unitNumber} (${user.displayName || user.email})`);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      // Reset form
      setUnitNumber('');
      setSelectedClass('');

    } catch (error) {
      console.error('Error submitting class:', error);
      setSnackbarMessage('Error logging class. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      py: 4,
      px: 2
    }}>
      {/* Header */}
      <Paper elevation={8} sx={{ 
        p: 4, 
        mb: 4, 
        borderRadius: 3,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        maxWidth: '600px',
        width: '100%'
      }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 'bold', 
          textAlign: 'center', 
          color: '#1976d2',
          mb: 2
        }}>
          🏥 Class Sign-In Kiosk
        </Typography>
        <Typography variant="h6" sx={{ 
          textAlign: 'center', 
          color: '#666',
          mb: 3
        }}>
          Enter your unit number to sign into a class
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="body1" sx={{ 
          textAlign: 'center', 
          color: '#1976d2',
          fontWeight: 'bold',
          mb: 1
        }}>
          Today's Date: {currentDate}
        </Typography>
      </Paper>

      {/* Main Form */}
      <Card elevation={8} sx={{ 
        maxWidth: '500px', 
        width: '100%',
        borderRadius: 3,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)'
      }}>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Unit Number Input */}
              <Grid item xs={12}>
                <TextField
                  label="Unit Number"
                  variant="outlined"
                  placeholder="Enter M## (e.g., M01, M15)"
                  fullWidth
                  value={unitNumber}
                  onChange={(e) => setUnitNumber(e.target.value.toUpperCase())}
                  required
                  sx={{ 
                    fontSize: '1.2rem',
                    '& .MuiInputBase-input': {
                      fontSize: '1.2rem',
                      textAlign: 'center',
                      fontWeight: 'bold'
                    }
                  }}
                  inputProps={{ 
                    maxLength: 3,
                    style: { textTransform: 'uppercase' }
                  }}
                  helperText="Format: M followed by 2 digits (e.g., M01, M15)"
                />
              </Grid>

              {/* Class Selection */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="class-select-label" sx={{ fontSize: '1.1rem' }}>
                    Select Class
                  </InputLabel>
                  <Select
                    labelId="class-select-label"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    required
                    sx={{ 
                      fontSize: '1.2rem',
                      '& .MuiSelect-select': {
                        fontSize: '1.2rem'
                      }
                    }}
                  >
                    {classes.map((classItem, index) => (
                      <MenuItem key={index} value={classItem} sx={{ fontSize: '1.1rem' }}>
                        {classItem}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button 
                  variant="contained" 
                  type="submit" 
                  fullWidth 
                  disabled={isLoading}
                  sx={{ 
                    padding: '15px', 
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1565c0 30%, #0d47a1 90%)',
                    }
                  }}
                >
                  {isLoading ? 'Signing In...' : 'Sign Into Class'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Paper elevation={4} sx={{ 
        mt: 4, 
        p: 3, 
        maxWidth: '500px', 
        width: '100%',
        borderRadius: 2,
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(5px)'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2' }}>
          Instructions:
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          • Enter your unit number in the format M## (e.g., M01, M15)
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          • Select the class you are attending
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          • Click "Sign Into Class" to log your attendance
        </Typography>
        <Typography variant="body2">
          • Your class will be logged for today's date in EST timezone
        </Typography>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%', fontSize: '1.1rem' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Kiosk;